import Image from 'next/image'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md'

export type TSelectOption = {
  readonly value: string
  readonly label: string
  readonly imgSrc?: string
}

export type TSearchableSelect = {
  readonly caption?: string
  readonly placeholder?: string
  readonly options: TSelectOption[]
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error?: string
  readonly disabled?: boolean
}

export default function SearchableSelect({
  caption,
  placeholder,
  options,
  value,
  onChange,
  error,
  disabled,
}: TSearchableSelect) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const selectRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleSelect = (optionValue: string) => {
    setIsOpen(false)
    onChange(optionValue)
  }

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!disabled && selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    },
    [disabled],
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <div className='relative flex w-full flex-col gap-1' ref={selectRef}>
      <span className={`text-base font-semibold text-pop-gray-1`}>{caption}</span>
      <div className='relative'>
        <div
          className={`w-full rounded-lg border ${
            error ? 'border-pop-error' : 'border-pop-gray-5'
          } cursor-pointer px-3 py-4`}
          onClick={handleToggle}
          tabIndex={0}
          data-testid='toggle-element'
        >
          <div className='sentry-mask flex items-center justify-between'>
            {isOpen ? (
              <input
                type='text'
                placeholder={
                  filteredOptions?.find((res) => res.value === value)?.label ??
                  placeholder ??
                  'Choose Answer'
                }
                className='no-spinner w-full font-light placeholder:font-extralight placeholder:text-pop-steel-blue focus:outline-none'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                disabled={disabled}
              />
            ) : (
              <div
                className={`font-light ${
                  options.find((opt) => opt.value === value)?.label ? '' : 'text-pop-neutral-gray'
                }`}
              >
                {value
                  ? options.find((opt) => opt.value === value)?.label
                  : placeholder ?? 'Choose Answer'}
              </div>
            )}
            <MdKeyboardArrowDown
              className='h-6 w-6 text-pop-grey-600'
              style={{ pointerEvents: disabled ? 'none' : 'auto' }}
            />
          </div>
        </div>
        {isOpen && (
          <div className='absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-md bg-white shadow-lg'>
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className='flex cursor-pointer flex-row items-center justify-between px-4 py-2 hover:bg-gray-200'
                onClick={() => handleSelect(option.value)}
                data-testid={`option-${index}`}
              >
                <div className='flex flex-row items-center gap-2'>
                  {option?.imgSrc && (
                    <Image
                      src={option.imgSrc}
                      width={70}
                      height={50}
                      alt={option?.value}
                      className='h-6 w-8'
                    />
                  )}
                  <span className={`${value === option.value ? 'font-bold' : 'font-normal'}`}>
                    {option.label}
                  </span>
                </div>
                {value === option.value && <MdCheck className='h-6 w-6 text-pop-blue-600' />}
              </div>
            ))}
          </div>
        )}
      </div>
      {!!error && <span className='text-xs text-pop-error'>{error}</span>}
    </div>
  )
}
